import { Box, Tooltip, useTheme } from '@mui/material';
import {
  DataGridPremiumProps,
  GridCellParams,
  GridColDef,
  GridPreProcessEditCellProps,
  GridRenderEditCellParams,
  GridRowId,
} from '@mui/x-data-grid-premium';
import { uniq } from 'lodash';
import { ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { ValidationError } from 'yup';

import { crudCellClassName } from '~/components/SolaForceDataGrid/hooks/useDataGridCRUDActions';

export interface GridCellCRUDValidationResult {
  rowId: GridRowId;
  error: ValidationError;
}

export type DataGridCRUDOnProcessRowUpdateErrorOutput =
  | GridCellCRUDValidationResult
  | ValidationError;

export type GridCellCRUDErrorState = {
  id: GridRowId;
  field: string;
  message: string;
};

type PropsType<T extends object> = {
  validationSchema: yup.ObjectSchema<yup.Shape<object | undefined, T>>;
};

type DataGridCRUDSyncErrorState = (
  errors: GridCellCRUDErrorState[],
  rowId?: GridRowId,
) => void;

export type DataGridCRUDColumnsValidation<T> = {
  onPreProcessEditCell: (
    fieldName: string,
  ) => GridColDef<T>['preProcessEditCellProps'];
  getEditCellErrorClassName: GridColDef<T>['cellClassName'];
  renderEditCellWithErrorTooltip: (
    render: (params: GridRenderEditCellParams) => ReactNode | undefined,
  ) => (editCellParams: GridRenderEditCellParams) => ReactNode | undefined;
};

export type DataGridCRUDActionsValidation = {
  syncCRUDErrorState: DataGridCRUDSyncErrorState;
  getEditRowErrors: (rowId: GridRowId) => GridCellCRUDErrorState[];
};

export type UseDataGridCRUDValidationOutput<T> = {
  columnsValidation: DataGridCRUDColumnsValidation<T>;
  actionsValidation: DataGridCRUDActionsValidation;
  dataGridProps: Pick<DataGridPremiumProps<T>, 'onProcessRowUpdateError'>;
  getEditRowErrors: (rowId: GridRowId) => GridCellCRUDErrorState[];
  getEditCellError: (rowId: GridRowId, field: string) => string | undefined;
};

export const useDataGridCRUDValidation = <T extends object>(
  props: PropsType<T>,
): UseDataGridCRUDValidationOutput<T> => {
  const { validationSchema } = props;

  const { t } = useTranslation();
  const theme = useTheme();

  const [crudErrors, setCrudErrors] = useState<GridCellCRUDErrorState[]>([]);

  const isCellErrorMatch =
    (error1: GridCellCRUDErrorState) => (error2: GridCellCRUDErrorState) =>
      error1.id === error2.id && error1.field === error2.field;

  const updateErrors = useCallback(
    (newErrors: GridCellCRUDErrorState[]) =>
      setCrudErrors((oldErrors) =>
        oldErrors
          .filter((err) => !newErrors.some(isCellErrorMatch(err)))
          .concat(newErrors),
      ),
    [setCrudErrors],
  );
  const clearErrors = useCallback(
    (clearErrors: GridCellCRUDErrorState[]) =>
      setCrudErrors((oldErrors) =>
        oldErrors.filter((err) => !clearErrors.some(isCellErrorMatch(err))),
      ),
    [setCrudErrors],
  );
  const syncErrors = useCallback(
    (syncErrors: GridCellCRUDErrorState[], rowId?: GridRowId) => {
      const rowIds = uniq([
        ...syncErrors.map((err) => err.id),
        ...(!!rowId ? [rowId] : []),
      ]);
      setCrudErrors((oldErrors) =>
        oldErrors.filter((err) => !rowIds.includes(err.id)).concat(syncErrors),
      );
    },
    [setCrudErrors],
  );

  const onPreProcessEditCell = useCallback(
    (fieldName: string) => async (params: GridPreProcessEditCellProps) => {
      const { props, id, otherFieldsProps } = params;

      const reconstructRowEditState = () => {
        const rowStateOther = !!otherFieldsProps
          ? Object.fromEntries(
              Object.entries(otherFieldsProps).map(([key, props]) => {
                return [key, props.value];
              }),
            )
          : {};

        return {
          [fieldName]: props.value,
          ...rowStateOther,
        } as T;
      };

      return validationSchema
        .validateAt(fieldName, reconstructRowEditState())
        .then(() => {
          clearErrors([{ id, field: fieldName, message: '' }]);
          return { ...props, error: false };
        })
        .catch((error) => {
          updateErrors([
            { id, field: fieldName, message: error?.message ?? '' },
          ]);
          return { ...props, error: false };
        });
    },
    [validationSchema, updateErrors, clearErrors],
  );

  const getEditCellErrorClassName = useCallback(
    (params: GridCellParams) => {
      const { id, field } = params;

      return crudErrors.some((err) => err.id === id && err.field === field)
        ? crudCellClassName.invalidCell
        : '';
    },
    [crudErrors],
  );

  const getEditRowErrors = useCallback(
    (rowId: GridRowId) => {
      return crudErrors.filter((e) => e.id === rowId);
    },
    [crudErrors],
  );

  const getEditCellError = useCallback(
    (rowId: GridRowId, field: string): string | undefined => {
      return crudErrors.find((e) => e.id === rowId && e.field === field)
        ?.message;
    },
    [crudErrors],
  );

  const renderEditCellWithErrorTooltip = useCallback(
    (render: (params: GridRenderEditCellParams) => ReactNode) =>
      (editCellParams: GridRenderEditCellParams) => {
        const { id, field } = editCellParams;

        return (
          <Tooltip
            title={getEditCellError(id, field)}
            arrow
            slotProps={{
              arrow: {
                sx: {
                  color: theme.palette.error.main,
                },
              },
              tooltip: {
                sx: {
                  backgroundColor: theme.palette.error.main,
                },
              },
            }}
          >
            <Box sx={{ width: '100%' }}>{render(editCellParams)}</Box>
          </Tooltip>
        );
      },
    [getEditCellError, theme],
  );

  const resolveErrors = useCallback(
    (resolverProps: {
      rowId?: GridRowId;
      error: ValidationError;
    }): GridCellCRUDErrorState[] => {
      const { rowId, error } = resolverProps;

      const resolveDataGridCellErrorsArray = (
        error: ValidationError,
      ): ValidationError[] => {
        if (!error?.path && !!error?.inner) {
          return error?.inner;
        }
        if (!!error?.path) {
          return [error];
        }

        return [error];
      };

      const resolveCellErrorState = (
        error: ValidationError,
      ): GridCellCRUDErrorState => {
        return {
          id: rowId ?? '',
          field: error?.path ?? '',
          message: error.message ?? t('formValidationFailed'),
        };
      };

      return resolveDataGridCellErrorsArray(error).map(resolveCellErrorState);
    },
    [t],
  );

  const onProcessRowUpdateError: DataGridPremiumProps<T>['onProcessRowUpdateError'] =
    useCallback(
      (validationResult: DataGridCRUDOnProcessRowUpdateErrorOutput) => {
        const { rowId, error }: GridCellCRUDValidationResult =
          validationResult.hasOwnProperty('rowId') &&
          validationResult.hasOwnProperty('error')
            ? (validationResult as GridCellCRUDValidationResult)
            : ({
                rowId: '',
                error: validationResult,
              } as GridCellCRUDValidationResult);

        const resolvedErrors = resolveErrors({ rowId, error });

        syncErrors(
          resolvedErrors.filter((e) => !!e.id && !!e.field),
          rowId,
        );
      },
      [resolveErrors, syncErrors],
    );

  return {
    columnsValidation: {
      onPreProcessEditCell,
      getEditCellErrorClassName,
      renderEditCellWithErrorTooltip,
    },
    actionsValidation: {
      syncCRUDErrorState: syncErrors,
      getEditRowErrors,
    },
    dataGridProps: {
      onProcessRowUpdateError,
    },
    getEditRowErrors,
    getEditCellError,
  };
};
